import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { useEffect } from 'react';

// Layout
import DashboardLayout from '@/layouts/DashboardLayout';
import AuthLayout from '@/layouts/AuthLayout';

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';

// Dashboard Pages
import DashboardHome from '@/pages/dashboard/DashboardHome';
import AnalyticsPage from '@/pages/analytics/AnalyticsPage';
import SessionsPage from '@/pages/sessions/SessionsPage';
import FunnelAnalysisPage from '@/pages/analytics/FunnelAnalysisPage';
import EmailCampaignsPage from '@/pages/email-campaigns/EmailCampaignsPage';
import EmailLogsPage from '@/pages/email-logs/EmailLogsPage';
import AttributionPage from '@/pages/analytics/AttributionPage';
import ROASDashboard from '@/pages/analytics/ROASDashboard';
import GetResponsePage from '@/pages/getresponse/GetResponsePage';
import BetterStackPage from '@/pages/betterstack/BetterStackPage';

// E-shop Pages
import EshopsPage from '@/pages/EshopsPage';
import EshopDetailPage from '@/pages/EshopDetailPage';
import EshopDataOverviewPage from '@/pages/EshopDataOverviewPage';

// Admin Pages
import UsersPage from '@/pages/admin/UsersPage';
import RolesPage from '@/pages/admin/RolesPage';
import CompaniesPage from '@/pages/admin/CompaniesPage';
import SettingsPage from '@/pages/settings/SettingsPage';

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (allows access only when not authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user } = useAuthStore();

  if (isAuthenticated) {
    // Redirect to user's default landing page or dashboard
    const landingPage = user?.roles?.[0]?.defaultLandingPage || '/dashboard';
    return <Navigate to={landingPage} replace />;
  }

  return <>{children}</>;
};

function App() {
  const { initializeAuth } = useAuthStore();

  useEffect(() => {
    // Initialize auth state from localStorage on mount
    initializeAuth();
  }, [initializeAuth]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/auth"
        element={
          <PublicRoute>
            <AuthLayout />
          </PublicRoute>
        }
      >
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
      </Route>

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardHome />} />

        {/* Analytics */}
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="analytics/funnel" element={<FunnelAnalysisPage />} />
        <Route path="analytics/attribution" element={<AttributionPage />} />
        <Route path="analytics/roas" element={<ROASDashboard />} />

        {/* Sessions */}
        <Route path="sessions" element={<SessionsPage />} />

        {/* Email Campaigns */}
        <Route path="email-campaigns" element={<EmailCampaignsPage />} />

        {/* Email Logs */}
        <Route path="email-logs" element={<EmailLogsPage />} />

        {/* Email Marketing (GetResponse, EmailOctopus, etc.) */}
        <Route path="email-marketing" element={<GetResponsePage />} />
        {/* Legacy route redirect for backward compatibility */}
        <Route path="getresponse" element={<Navigate to="/email-marketing" replace />} />

        {/* BetterStack */}
        <Route path="betterstack" element={<BetterStackPage />} />

        {/* E-shops */}
        <Route path="eshops" element={<EshopsPage />} />
        <Route path="eshops/:eshopId" element={<EshopDetailPage />} />
        <Route path="eshops/:eshopId/data-overview" element={<EshopDataOverviewPage />} />

        {/* Admin */}
        <Route path="admin/users" element={<UsersPage />} />
        <Route path="admin/roles" element={<RolesPage />} />
        <Route path="admin/companies" element={<CompaniesPage />} />

        {/* Settings */}
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App;
